import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import { type ColumnDef, DataTable } from '@features/billing/components/v2/shared';
import { PageGuideLink } from '@features/knowledge-base/links';
import { useGetEligibilityWorklistQuery } from '@features/patient-coverages';
import { safeWorklistItem } from '@features/patient-coverages/utils/patientCost';
import {
  useGetTutorialProgressQuery,
  useSubjectLanguageVariants,
  useUpdateTutorialProgressMutation,
} from '@features/user';
import { Avatar } from '@jotpsych/ui';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type {
  GetV2ProviderEligibilityWorklistApiArg,
  WorklistItem,
} from '@store/generated/eligibility';
import { useModal } from '@store/hooks';
import { addDays, format, isSameDay, parseISO, startOfDay } from 'date-fns';

import { useEligibilityActions } from '../hooks/useEligibilityActions';
import type { BulkCheckTargetStatus } from '../hooks/useRunEligibilityChecksBatch';
import { useRunEligibilityChecksBatch } from '../hooks/useRunEligibilityChecksBatch';
import { ELIG_TOKENS, getReadinessTreatment } from '../theme/readinessTokens';
import {
  autoExpandForState,
  deriveWorklistRowState,
  isRunnableState,
  type WorklistAutoExpand,
  type WorklistRowState,
} from '../utils/worklistRowState';

import {
  ELIGIBILITY_CHECKS_TUTORIAL_ID,
  ELIGIBILITY_CHECKS_TUTORIAL_VERSION,
  EligibilityChecksOnboardingModal,
  type EligibilityChecksOnboardingMode,
} from './EligibilityChecksOnboardingModal';
import { ReadinessIndicator } from './ReadinessIndicator';

type RangeKey = 'today' | 'tomorrow' | 'week';
type WorkFilterKey = 'all' | 'needs-run';

const DEFAULT_RANGE: RangeKey = 'week';
const PROVIDER_BADGE_COLORS = ['#6C00CC', '#D46D27', '#2563EB', '#059669', '#DB2777'] as const;
const TOP_ALIGNED_BADGE_PIXEL_OFFSET = 4;

type EligibilityTableRow = {
  patient_id: string;
  visit: string;
  patient: string;
  provider: string;
  status: string;
  action: string;
  _item: WorklistItem;
  _state: WorklistRowState;
  _bulkStatus?: BulkCheckTargetStatus;
  _bulkErrorMessage?: string;
  _isResolved: boolean;
};

const ELIGIBILITY_TABLE_COLUMNS: ColumnDef[] = [
  { key: 'visit', label: 'Visit', width: 'minmax(8.5rem, 0.65fr)', required: true },
  {
    key: 'patient',
    label: 'Patient',
    width: 'minmax(12rem, 0.95fr)',
    type: 'patient',
    required: true,
  },
  { key: 'provider', label: 'Provider', width: 'minmax(10rem, 0.75fr)' },
  { key: 'status', label: 'Status', width: 'minmax(16rem, 1.2fr)', type: 'action', required: true },
  { key: 'action', label: 'Action', width: 'minmax(8rem, 0.55fr)', required: true },
];

const STATUS_UI: Record<
  WorklistRowState,
  {
    label: string;
    color: string;
    tint: string;
    action: string;
  }
> = {
  verified_lock_active: {
    label: 'Verified',
    color: ELIG_TOKENS.green,
    tint: ELIG_TOKENS.greenTint,
    action: 'View',
  },
  collected: {
    label: 'Collected',
    color: ELIG_TOKENS.green,
    tint: ELIG_TOKENS.greenTint,
    action: 'View',
  },
  self_pay: {
    label: 'Self-pay',
    color: ELIG_TOKENS.green,
    tint: ELIG_TOKENS.greenTint,
    action: 'View',
  },
  awaiting_check: {
    label: 'Awaiting check',
    color: ELIG_TOKENS.gray,
    tint: ELIG_TOKENS.grayTint,
    action: 'Run check',
  },
  needs_coverage: {
    label: 'Needs coverage',
    color: ELIG_TOKENS.gray,
    tint: ELIG_TOKENS.grayTint,
    action: 'Add coverage',
  },
  attention: {
    label: 'Needs verification',
    color: ELIG_TOKENS.amber,
    tint: ELIG_TOKENS.amberTint,
    action: 'Verify',
  },
  verified_lock_expired: {
    label: 'Verification expired',
    color: ELIG_TOKENS.amber,
    tint: ELIG_TOKENS.amberTint,
    action: 'Re-verify',
  },
  collect: {
    label: 'Collect',
    color: ELIG_TOKENS.green,
    tint: ELIG_TOKENS.greenTint,
    action: 'Collect',
  },
  blocked: {
    label: 'Blocked',
    color: ELIG_TOKENS.red,
    tint: ELIG_TOKENS.redTint,
    action: 'Fix',
  },
  info: {
    label: 'Info',
    color: ELIG_TOKENS.gray,
    tint: ELIG_TOKENS.grayTint,
    action: 'View',
  },
};

/**
 * Eligibility-checks tab inside JotBill.
 *
 * Dense encounter-ordered surface for the upcoming window. Rows stay in
 * place while checks run, with optional local filters for date range and
 * "Needs run" throughput work.
 */
export const VerificationsTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialNeedsRun = searchParams.get('segment') === 'awaiting_check';
  const [range, setRange] = useState<RangeKey>(DEFAULT_RANGE);
  const [workFilter, setWorkFilter] = useState<WorkFilterKey>(
    initialNeedsRun ? 'needs-run' : 'all',
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [resolvedSet, setResolvedSet] = useState<Set<string>>(new Set());
  const [onboardingMode, setOnboardingMode] = useState<EligibilityChecksOnboardingMode | null>(
    null,
  );
  const interactionSnapshotsRef = useRef<Map<string, string>>(new Map());
  const hasEvaluatedOnboardingRef = useRef(false);

  const queryArgs: GetV2ProviderEligibilityWorklistApiArg = useMemo(
    () => ({
      days: 7,
      perPage: 100,
    }),
    [],
  );

  const {
    data: rawData,
    isLoading,
    refetch,
  } = useGetEligibilityWorklistQuery(queryArgs, {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });
  const data = useMemo(
    () => (rawData ? { ...rawData, items: rawData.items?.map(safeWorklistItem) } : rawData),
    [rawData],
  );
  const {
    data: tutorialProgressData,
    isLoading: isTutorialProgressLoading,
    isError: isTutorialProgressError,
  } = useGetTutorialProgressQuery(undefined);
  const [updateTutorialProgress] = useUpdateTutorialProgressMutation();
  const { openInvoice, openLogCollection, openDrawer } = useEligibilityActions();
  const { open: openModal } = useModal();
  const { eligibilityChecks } = useEventTracking();
  const { singularCap } = useSubjectLanguageVariants();
  const bulkRun = useRunEligibilityChecksBatch();

  const rawItems = useMemo(() => data?.items ?? [], [data]);
  const rangeItems = useMemo(
    () =>
      rawItems
        .filter(item => isInRange(item, range))
        .sort((a, b) => (a.next_appointment_at ?? '').localeCompare(b.next_appointment_at ?? '')),
    [rawItems, range],
  );
  const visibleItems = useMemo(
    () => (workFilter === 'needs-run' ? rangeItems.filter(isRunnableItem) : rangeItems),
    [rangeItems, workFilter],
  );
  const runnableVisibleItems = useMemo(() => visibleItems.filter(isRunnableItem), [visibleItems]);
  const counts = useMemo(() => summarize(rangeItems), [rangeItems]);
  const selectedRunnableIds = useMemo(
    () => runnableVisibleItems.map(item => item.patient_id).filter(id => selected.has(id)),
    [runnableVisibleItems, selected],
  );
  const selectedCount = selectedRunnableIds.length;

  const hasFiredOpenEvent = useRef(false);
  useEffect(() => {
    if (hasEvaluatedOnboardingRef.current || isTutorialProgressLoading || isTutorialProgressError) {
      return;
    }

    const tutorialProgress =
      tutorialProgressData?.tutorial_progress?.[ELIGIBILITY_CHECKS_TUTORIAL_ID];
    const isCompleteOrDismissed =
      tutorialProgress?.status === 'completed' || tutorialProgress?.status === 'dismissed';
    const isOlderVersion =
      Boolean(tutorialProgress) &&
      (tutorialProgress?.version ?? 0) < ELIGIBILITY_CHECKS_TUTORIAL_VERSION;

    if (!tutorialProgress || !isCompleteOrDismissed || isOlderVersion) {
      setOnboardingMode('invite');
    }

    hasEvaluatedOnboardingRef.current = true;
  }, [isTutorialProgressError, isTutorialProgressLoading, tutorialProgressData]);

  const updateOnboardingProgress = useCallback(
    (status: 'in_progress' | 'dismissed' | 'completed', currentStep?: number) => {
      void updateTutorialProgress({
        tutorial_id: ELIGIBILITY_CHECKS_TUTORIAL_ID,
        updated_fields: {
          status,
          version: ELIGIBILITY_CHECKS_TUTORIAL_VERSION,
          ...(currentStep !== undefined ? { current_step: currentStep } : {}),
        },
      });
    },
    [updateTutorialProgress],
  );

  const handleOnboardingStart = useCallback(() => {
    updateOnboardingProgress('in_progress', 0);
  }, [updateOnboardingProgress]);

  const handleOnboardingDismiss = useCallback(() => {
    setOnboardingMode(null);
    updateOnboardingProgress('dismissed');
  }, [updateOnboardingProgress]);

  const handleOnboardingComplete = useCallback(() => {
    setOnboardingMode(null);
    updateOnboardingProgress('completed', 5);
  }, [updateOnboardingProgress]);

  useEffect(() => {
    if (hasFiredOpenEvent.current || !data) return;
    eligibilityChecks.worklistOpened?.({
      days: 7,
      total_patients: data.totals.total_patients_in_window,
      needs_attention: data.totals.needs_attention,
      verified: data.totals.verified,
    });
    hasFiredOpenEvent.current = true;
  }, [data, eligibilityChecks]);

  useEffect(() => {
    if (!data?.items) return;
    const snapshots = interactionSnapshotsRef.current;
    if (snapshots.size === 0) return;
    setResolvedSet(prev => {
      let next: Set<string> | null = null;
      for (const item of data.items ?? []) {
        const wasReadiness = snapshots.get(item.patient_id);
        if (wasReadiness === undefined) continue;
        const nowReadiness = item.readiness ?? 'null';
        if (wasReadiness !== nowReadiness && !prev.has(item.patient_id)) {
          if (!next) next = new Set(prev);
          next.add(item.patient_id);
        }
      }
      return next ?? prev;
    });
  }, [data]);

  const inputFor = useCallback(
    (item: WorklistItem) => ({
      patientId: item.patient_id,
      patientName: item.patient_name,
      encounterId: item.next_encounter_id ?? undefined,
      appointmentId: item.next_appointment_id ?? undefined,
      memberObligationCents: item.member_obligation_cents ?? null,
      source: 'worklist' as const,
    }),
    [],
  );

  const handleWorkFilterChange = useCallback(
    (next: WorkFilterKey) => {
      if (next === workFilter) return;
      eligibilityChecks.worklistSegmentChanged?.({
        from_segment: workFilter === 'needs-run' ? 'awaiting_check' : 'all',
        to_segment: next === 'needs-run' ? 'awaiting_check' : 'all',
        to_count:
          next === 'needs-run' ? rangeItems.filter(isRunnableItem).length : rangeItems.length,
      });
      setWorkFilter(next);
      setSelected(new Set());
      bulkRun.reset();
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          if (next === 'needs-run') params.set('segment', 'awaiting_check');
          else params.delete('segment');
          return params;
        },
        { replace: true },
      );
    },
    [bulkRun, eligibilityChecks, rangeItems, setSearchParams, workFilter],
  );

  const handleRowClick = useCallback(
    (item: WorklistItem, autoExpand: WorklistAutoExpand, state: WorklistRowState) => {
      interactionSnapshotsRef.current.set(item.patient_id, item.readiness ?? 'null');

      eligibilityChecks.worklistRowClicked?.({
        patient_id: item.patient_id,
        task_priority: item.task_priority,
        row_state: state,
        action_verb: item.action_verb ?? undefined,
      });
      eligibilityChecks.worklistDrawerOpened?.({
        patient_id: item.patient_id,
        row_state: state,
        auto_expand: autoExpand,
      });

      if (state === 'needs_coverage') {
        openModal(MODAL_TYPES.INSURANCE_EDIT, { patientId: item.patient_id });
        return;
      }

      openDrawer(inputFor(item), {
        verificationForm: autoExpand === 'verification',
      });
    },
    [eligibilityChecks, inputFor, openDrawer, openModal],
  );

  const handleInvoice = useCallback(
    (item: WorklistItem) => {
      interactionSnapshotsRef.current.set(item.patient_id, item.readiness ?? 'null');
      eligibilityChecks.worklistInvoiceClicked?.({
        patient_id: item.patient_id,
        task_priority: item.task_priority,
      });
      openInvoice(inputFor(item));
    },
    [eligibilityChecks, inputFor, openInvoice],
  );

  const handleLogCollection = useCallback(
    (item: WorklistItem) => {
      const state = deriveWorklistRowState(item);
      eligibilityChecks.worklistRowClicked?.({
        patient_id: item.patient_id,
        task_priority: item.task_priority,
        row_state: state,
        action_verb: 'Log collection',
      });
      openLogCollection(inputFor(item));
    },
    [eligibilityChecks, inputFor, openLogCollection],
  );

  const runChecks = useCallback(
    async (itemsToRun: WorklistItem[]) => {
      const targets = itemsToRun.filter(isRunnableItem).map(item => ({
        patient_id: item.patient_id,
        encounter_id: item.next_encounter_id ?? undefined,
      }));
      if (targets.length === 0) return;
      targets.forEach(target => {
        const item = rawItems.find(row => row.patient_id === target.patient_id);
        if (item) interactionSnapshotsRef.current.set(item.patient_id, item.readiness ?? 'null');
      });
      await bulkRun.runBatch(targets);
      void refetch();
      setSelected(new Set());
    },
    [bulkRun, rawItems, refetch],
  );

  const rows: EligibilityTableRow[] = useMemo(
    () =>
      visibleItems.map(item => {
        const state = deriveWorklistRowState(item);
        const isCollect = state === 'collect';
        const isRunnable = isRunnableState(state);
        const appointment = item.next_appointment_at ? parseISO(item.next_appointment_at) : null;
        return {
          patient_id: item.patient_id,
          visit: appointment ? format(appointment, 'EEE h:mm a') : 'Unscheduled',
          patient: item.patient_name,
          provider: item.provider_name ?? '-',
          status: item.readiness_label ?? STATUS_UI[state].label,
          action: isCollect
            ? `Invoice ${singularCap}`
            : isRunnable
              ? 'Run check'
              : (item.action_verb ?? STATUS_UI[state].action),
          _item: item,
          _state: state,
          _bulkStatus: bulkRun.statuses[item.patient_id],
          _bulkErrorMessage: getBulkErrorMessage(
            (bulkRun.results as Record<string, unknown>)[item.patient_id],
          ),
          _isResolved: resolvedSet.has(item.patient_id),
        };
      }),
    [bulkRun.results, bulkRun.statuses, resolvedSet, singularCap, visibleItems],
  );

  const renderCell = useCallback(
    (column: ColumnDef, row: Record<string, unknown>) => {
      const typedRow = row as EligibilityTableRow;
      const item = typedRow._item;
      const state = typedRow._state;
      const status = STATUS_UI[state];
      const appointment = item.next_appointment_at ? parseISO(item.next_appointment_at) : null;
      const isRunnable = isRunnableState(state);
      const isCollect = state === 'collect';
      const isRunning = typedRow._bulkStatus === 'running';

      if (column.key === 'visit') {
        return (
          <Stack spacing={0} sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 650, color: ELIG_TOKENS.ink }}>
              {appointment ? format(appointment, 'EEE h:mm a') : 'Unscheduled'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: ELIG_TOKENS.inkSoft, lineHeight: 1.15 }} noWrap>
              {formatVisitDetail(item)}
            </Typography>
          </Stack>
        );
      }

      if (column.key === 'patient') {
        return <PatientIdentity item={item} />;
      }

      if (column.key === 'provider') {
        return (
          <ProviderIdentity
            providerName={item.provider_name ?? null}
            licenseLevel={item.provider_license_level ?? null}
          />
        );
      }

      if (column.key === 'status') {
        return (
          <Box
            sx={{
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
            }}
          >
            <Stack direction="row" spacing={0.75} sx={{ minWidth: 0, alignItems: 'center' }}>
              <StatusGlyph item={item} state={state} />
              <Typography sx={{ fontSize: 12, fontWeight: 650, color: status.color }} noWrap>
                {item.readiness_label ?? status.label}
              </Typography>
              <BulkStatusIndicator
                status={typedRow._bulkStatus}
                errorMessage={typedRow._bulkErrorMessage}
              />
            </Stack>
            <Tooltip title={item.summary}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: ELIG_TOKENS.inkSoft,
                  lineHeight: 1.15,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.summary}
              </Typography>
            </Tooltip>
          </Box>
        );
      }

      if (column.key === 'action') {
        return (
          <Stack
            direction="column"
            spacing={0.1}
            sx={{ justifyContent: 'flex-start', alignItems: 'flex-start' }}
          >
            <ActionButton
              label={typedRow.action}
              isPrimary={isRunnable}
              isRunning={isRunning}
              onClick={
                isCollect
                  ? () => handleInvoice(item)
                  : isRunnable
                    ? () => void runChecks([item])
                    : () => handleRowClick(item, autoExpandForState(state), state)
              }
            />
            {isCollect && (
              <ActionButton label="Log collection" onClick={() => handleLogCollection(item)} />
            )}
          </Stack>
        );
      }

      return null;
    },
    [handleInvoice, handleLogCollection, handleRowClick, runChecks],
  );

  const rowSx = useCallback((row: Record<string, unknown>) => {
    const typedRow = row as EligibilityTableRow;
    const status = STATUS_UI[typedRow._state];
    const stripeWidth = stripeWidthForRow(typedRow._item, typedRow._state);
    return {
      position: 'relative',
      alignItems: 'start',
      minHeight: 54,
      py: 0.75,
      bgcolor: status.tint,
      opacity: typedRow._isResolved ? 0.58 : 1,
      '&:hover': {
        bgcolor: stripeWidth > 0 ? status.tint : ELIG_TOKENS.bg,
      },
      '&::before': stripeWidth
        ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: stripeWidth,
            bgcolor: status.color,
          }
        : undefined,
    };
  }, []);

  const noVisits = rawItems.length === 0;

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="rounded" height={42} width={400} />
        <Skeleton variant="rounded" height={20} width={320} />
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  if (noVisits) {
    return (
      <EmptyHero
        title="No upcoming visits"
        body="Nothing scheduled in the next 7 days."
        tone="neutral"
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <EligibilityChecksOnboardingModal
        open={onboardingMode !== null}
        initialMode={onboardingMode ?? 'invite'}
        onStart={handleOnboardingStart}
        onDismiss={handleOnboardingDismiss}
        onComplete={handleOnboardingComplete}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'flex-end' } }}
      >
        <Box>
          {/* Title row: the page sentence (GDN-3704) follows the title; flex gap reserves nothing while it is absent. */}
          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 650, color: ELIG_TOKENS.ink }}>
              Eligibility checks
            </Typography>
            <PageGuideLink page="eligibilityChecks" />
          </Stack>
          <Typography sx={{ fontSize: 13, color: ELIG_TOKENS.inkSoft, mt: 0.25 }}>
            {formatRangeCaption(range, rangeItems)}
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={range}
          onChange={(_, next: RangeKey | null) => {
            if (next) {
              setRange(next);
              setSelected(new Set());
            }
          }}
          sx={segmentedControlSx}
        >
          <ToggleButton value="today">Today</ToggleButton>
          <ToggleButton value="tomorrow">Tomorrow</ToggleButton>
          <ToggleButton value="week">This week</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <MetricPill
          label="Good"
          value={counts.good}
          color={ELIG_TOKENS.green}
          bg={ELIG_TOKENS.greenSoft}
        />
        <MetricPill
          label="Run"
          value={counts.run}
          color={ELIG_TOKENS.gray}
          bg={ELIG_TOKENS.graySoft}
        />
        <MetricPill
          label="Verify"
          value={counts.verify}
          color={ELIG_TOKENS.amber}
          bg={ELIG_TOKENS.amberSoft}
        />
        <MetricPill
          label="Review"
          value={counts.notGood}
          color={ELIG_TOKENS.red}
          bg={ELIG_TOKENS.redSoft}
        />
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' } }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={workFilter}
          onChange={(_, next: WorkFilterKey | null) => {
            if (next) handleWorkFilterChange(next);
          }}
          sx={subFilterSx}
        >
          <ToggleButton value="all">All checks</ToggleButton>
          <ToggleButton value="needs-run">
            Needs run ({rangeItems.filter(isRunnableItem).length})
          </ToggleButton>
        </ToggleButtonGroup>

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Typography sx={{ fontSize: 12, color: ELIG_TOKENS.inkSoft }}>
            {selectedCount} selected
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={selectedCount === 0 || bulkRun.isRunning}
            onClick={() =>
              void runChecks(runnableVisibleItems.filter(item => selected.has(item.patient_id)))
            }
            sx={bulkButtonSx}
          >
            Run selected
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={runnableVisibleItems.length === 0 || bulkRun.isRunning}
            onClick={() => void runChecks(runnableVisibleItems)}
            sx={{
              ...bulkButtonSx,
              bgcolor: ELIG_TOKENS.midnight,
              '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.92)' },
            }}
          >
            Run all ({runnableVisibleItems.length})
          </Button>
        </Stack>
      </Stack>

      <Box data-testid="verification-table-scroll" sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1060 }}>
          <DataTable
            columns={ELIGIBILITY_TABLE_COLUMNS}
            rows={rows}
            idKey="patient_id"
            selectedIds={selected}
            onSelectionChange={setSelected}
            isRowSelectable={row =>
              isRunnableItem((row as EligibilityTableRow)._item) &&
              bulkRun.statuses[(row as EligibilityTableRow).patient_id] !== 'running'
            }
            onRowClick={row => {
              const typedRow = row as EligibilityTableRow;
              handleRowClick(typedRow._item, autoExpandForState(typedRow._state), typedRow._state);
            }}
            renderCell={renderCell}
            rowSx={rowSx}
          />
        </Box>
      </Box>
    </Box>
  );
};

const PatientIdentity: React.FC<{ item: WorklistItem }> = ({ item }) => {
  const { firstName, lastName } = splitDisplayName(item.patient_name);

  return (
    <Stack direction="row" spacing={0.75} sx={{ minWidth: 0, alignItems: 'flex-start' }}>
      <Avatar
        id={item.patient_id}
        firstName={firstName}
        lastName={lastName}
        sx={{
          width: 28,
          height: 28,
          fontWeight: 650,
          flexShrink: 0,
          transform: `translateY(${TOP_ALIGNED_BADGE_PIXEL_OFFSET}px)`,
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 650, color: ELIG_TOKENS.ink }} noWrap>
          {item.patient_name}
        </Typography>
        <Typography sx={{ fontSize: 12, color: ELIG_TOKENS.inkSoft, lineHeight: 1.15 }} noWrap>
          {item.payer_name ?? 'No payer'}
        </Typography>
      </Box>
    </Stack>
  );
};

const ProviderIdentity: React.FC<{
  providerName: string | null;
  licenseLevel?: string | null;
}> = ({ providerName, licenseLevel }) => (
  <Stack direction="row" spacing={0.75} sx={{ minWidth: 0, alignItems: 'flex-start' }}>
    {providerName ? <ProviderBadge providerName={providerName} /> : <MissingProviderBadge />}
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 650, color: ELIG_TOKENS.ink }} noWrap>
        {providerName ?? '-'}
      </Typography>
      {licenseLevel && (
        <Typography sx={{ fontSize: 12, color: ELIG_TOKENS.inkSoft, lineHeight: 1.15 }} noWrap>
          {licenseLevel}
        </Typography>
      )}
    </Box>
  </Stack>
);

const ProviderBadge: React.FC<{ providerName: string }> = ({ providerName }) => {
  const color = getProviderBadgeColor(providerName);

  return (
    <Box
      component="span"
      aria-label={providerName}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 18,
        minWidth: 24,
        px: 0.5,
        borderRadius: '4px',
        bgcolor: color,
        color: '#fff',
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1,
        flexShrink: 0,
        transform: `translateY(${TOP_ALIGNED_BADGE_PIXEL_OFFSET}px)`,
      }}
    >
      {providerInitials(providerName)}
    </Box>
  );
};

const MissingProviderBadge = () => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 18,
      minWidth: 24,
      px: 0.5,
      borderRadius: '4px',
      border: `1.5px solid ${ELIG_TOKENS.inkFaint}`,
      color: ELIG_TOKENS.inkFaint,
      fontSize: 10,
      fontWeight: 700,
      lineHeight: 1,
      flexShrink: 0,
      transform: `translateY(${TOP_ALIGNED_BADGE_PIXEL_OFFSET}px)`,
    }}
  >
    --
  </Box>
);

const StatusGlyph: React.FC<{ item: WorklistItem; state: WorklistRowState }> = ({
  item,
  state,
}) => {
  if (item.readiness) return <ReadinessIndicator readiness={item.readiness} size={10} />;
  if (state === 'self_pay') return <NeutralDot color={ELIG_TOKENS.green} />;
  return <NeutralDot color={ELIG_TOKENS.gray} />;
};

const NeutralDot: React.FC<{ color: string }> = ({ color }) => (
  <Box
    sx={{
      width: 10,
      height: 10,
      borderRadius: '50%',
      border: `2px solid ${color}`,
      flexShrink: 0,
    }}
  />
);

const ActionButton: React.FC<{
  label: string;
  isPrimary?: boolean;
  isRunning?: boolean;
  onClick: () => void;
}> = ({ label, isPrimary = false, isRunning = false, onClick }) => (
  <Button
    size="small"
    variant={isPrimary ? 'contained' : 'text'}
    startIcon={isRunning ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined}
    onClick={event => {
      event.stopPropagation();
      onClick();
    }}
    sx={{
      minWidth: 0,
      textTransform: 'none',
      fontSize: 12,
      fontWeight: 650,
      whiteSpace: 'nowrap',
      justifyContent: 'flex-start',
      px: isPrimary ? 1.25 : 0,
      py: isPrimary ? 0.55 : 0,
      ...(isPrimary
        ? {
            bgcolor: ELIG_TOKENS.midnight,
            '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.92)' },
          }
        : {
            color: ELIG_TOKENS.midnight,
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }),
    }}
  >
    {isRunning ? 'Running' : label}
  </Button>
);

const BulkStatusIndicator: React.FC<{
  status?: BulkCheckTargetStatus;
  errorMessage?: string;
}> = ({ status, errorMessage }) => {
  if (!status || status === 'idle') return null;
  if (status === 'running') return <CircularProgress size={14} thickness={5} />;
  if (status === 'success')
    return <CheckCircleOutlineIcon sx={{ fontSize: 16, color: ELIG_TOKENS.green }} />;
  if (status === 'deduped') {
    return (
      <Tooltip title="Already up to date - skipped" arrow>
        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: ELIG_TOKENS.inkFaint }} />
      </Tooltip>
    );
  }
  if (status === 'self_pay_skipped') {
    return (
      <Tooltip title="Self-pay - no eligibility check needed" arrow>
        <RemoveCircleOutlineIcon sx={{ fontSize: 16, color: ELIG_TOKENS.inkFaint }} />
      </Tooltip>
    );
  }
  if (status === 'member_id_missing') {
    return (
      <Tooltip title="No member ID - add it from the card. No check was sent." arrow>
        <ErrorOutlineIcon
          aria-label="No member ID"
          sx={{ fontSize: 16, color: ELIG_TOKENS.amber }}
        />
      </Tooltip>
    );
  }
  if (status === 'failed') {
    return (
      <Tooltip title={errorMessage ?? 'Check failed'} arrow>
        <ErrorOutlineIcon sx={{ fontSize: 16, color: ELIG_TOKENS.red }} />
      </Tooltip>
    );
  }
  return null;
};

interface EmptyHeroProps {
  title: string;
  body: string;
  tone: 'success' | 'neutral';
}

const EmptyHero: React.FC<EmptyHeroProps> = ({ title, body, tone }) => {
  const styles =
    tone === 'success'
      ? {
          bg: '#F0FDF4',
          border: '#BBF7D0',
          icon: '#16A34A',
          title: '#166534',
          body: '#16A34A',
        }
      : {
          bg: '#F9FAFB',
          border: '#E5E7EB',
          icon: '#9CA3AF',
          title: '#374151',
          body: '#6B7280',
        };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          bgcolor: styles.bg,
          borderRadius: '10px',
          border: `1px solid ${styles.border}`,
          p: 4,
          textAlign: 'center',
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 40, color: styles.icon, mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: styles.title, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: styles.body }}>
          {body}
        </Typography>
      </Box>
    </Box>
  );
};

const MetricPill: React.FC<{
  label: string;
  value: number;
  color: string;
  bg: string;
}> = ({ label, value, color, bg }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      bgcolor: bg,
      color,
      borderRadius: 999,
      px: 1.25,
      py: 0.5,
      fontSize: 12,
      fontWeight: 650,
    }}
  >
    <Box component="span">{value}</Box>
    <Box component="span">{label}</Box>
  </Box>
);

const bulkButtonSx = {
  minWidth: 0,
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 650,
  px: 1.25,
  py: 0.55,
  borderColor: ELIG_TOKENS.lineStrong,
};

const segmentedControlSx = {
  alignSelf: { xs: 'flex-start', md: 'auto' },
  bgcolor: ELIG_TOKENS.card,
  '& .MuiToggleButton-root': {
    px: 2,
    py: 0.75,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'none',
    color: ELIG_TOKENS.inkSoft,
    borderColor: ELIG_TOKENS.lineStrong,
    '&.Mui-selected': {
      bgcolor: ELIG_TOKENS.midnight,
      color: '#fff',
      '&:hover': { bgcolor: ELIG_TOKENS.midnight },
    },
  },
};

const subFilterSx = {
  alignSelf: { xs: 'flex-start', md: 'auto' },
  bgcolor: ELIG_TOKENS.card,
  '& .MuiToggleButton-root': {
    px: 1.5,
    py: 0.55,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'none',
    color: ELIG_TOKENS.inkSoft,
    borderColor: ELIG_TOKENS.lineStrong,
    '&.Mui-selected': {
      bgcolor: ELIG_TOKENS.graySoft,
      color: ELIG_TOKENS.ink,
      '&:hover': { bgcolor: ELIG_TOKENS.graySoft },
    },
  },
};

function isInRange(item: WorklistItem, range: RangeKey): boolean {
  if (!item.next_appointment_at) return false;
  const appointment = parseISO(item.next_appointment_at);
  const today = startOfDay(new Date());
  if (range === 'today') return isSameDay(appointment, today);
  if (range === 'tomorrow') return isSameDay(appointment, addDays(today, 1));
  return appointment >= today && appointment < addDays(today, 7);
}

function isRunnableItem(item: WorklistItem): boolean {
  return isRunnableState(deriveWorklistRowState(item));
}

function summarize(items: WorklistItem[]) {
  return items.reduce(
    (acc, item) => {
      const state = deriveWorklistRowState(item);
      if (state === 'verified_lock_active' || state === 'collected' || state === 'self_pay') {
        acc.good += 1;
      } else if (state === 'awaiting_check') {
        acc.run += 1;
      } else if (
        state === 'attention' ||
        state === 'verified_lock_expired' ||
        state === 'needs_coverage' ||
        state === 'collect' ||
        state === 'info'
      ) {
        acc.verify += 1;
      } else if (state === 'blocked') {
        acc.notGood += 1;
      }
      return acc;
    },
    { good: 0, run: 0, verify: 0, notGood: 0 },
  );
}

function formatRangeCaption(range: RangeKey, items: WorklistItem[]): string {
  if (items.length === 0) return 'No visits in range';
  if (range === 'today') return `${items.length} visits today`;
  if (range === 'tomorrow') return `${items.length} visits tomorrow`;
  const first = parseISO(items[0].next_appointment_at ?? '');
  const last = parseISO(items[items.length - 1].next_appointment_at ?? '');
  return `${items.length} visits - ${format(first, 'MMM d')} to ${format(last, 'MMM d')}`;
}

function stripeWidthForRow(item: WorklistItem, state: WorklistRowState): 0 | 3 | 12 {
  if (item.readiness) return getReadinessTreatment(item.readiness)?.stripeWidthPx ?? 0;
  if (state === 'needs_coverage') return 3;
  return 0;
}

function getBulkErrorMessage(result: unknown): string | undefined {
  if (!result || typeof result !== 'object' || !('error_message' in result)) return undefined;
  const errorMessage = (result as { error_message?: unknown }).error_message;
  return typeof errorMessage === 'string' ? errorMessage : undefined;
}

function splitDisplayName(name: string): { firstName: string; lastName: string } {
  const [firstName = '', ...rest] = name.trim().split(/\s+/);
  return { firstName, lastName: rest.join(' ') };
}

function providerInitials(name: string): string {
  return name
    .replace(/^(Dr|Mr|Mrs|Ms|Mx)\.?\s+/i, '')
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function getProviderBadgeColor(name: string): string {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) >>> 0;
  }
  return PROVIDER_BADGE_COLORS[hash % PROVIDER_BADGE_COLORS.length];
}

function formatVisitDetail(item: WorklistItem): string {
  const parts: string[] = [];
  parts.push(item.encounter_type ?? 'Visit');
  if (item.duration_minutes) parts.push(`${item.duration_minutes}m`);
  return parts.join(' · ');
}
